import { get, isEmpty, noop } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css, withTheme } from 'styled-components';
import cookie from 'js-cookie';

import { UniversalSearch } from '@zocdoc/components/src/universalsearch';
import { ThemedInsurancePicker as InsurancePicker } from '@zocdoc/components/src/InsurancePicker';
import LocationPicker from '@zocdoc/components/src/LocationPicker';
import { MagnifyingGlass, Arrow } from '@zocdoc/components/src/icons';
import { FontFamily } from '@zocdoc/components/src/typography';
import { breakpoint } from '@zocdoc/components/src/grid';
import { INPUT_HEIGHT } from '@zocdoc/components/src/picker-constants';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import { PINA_INSURANCE_PICKER_PERSIST_CHOSEN_CARRIER, PINA_INSURANCE_PICKER_USE_DROPDOWN_CHEVRON } from '../../../config/ab/experiments';
import isMobileBreakpoint from '../../../utils/isMobileBreakpoint';
import isZocdocBrand from '../../../utils/isZocdocBrand';
import { fetchCurrentLocation } from '../../utils';
import { pageName, CookieNames } from '../../constants';
import * as EventTrackingConstants from '../../../EventTrackingConstants';
import { getPendingSearchParameters } from '../../state';
import {
    getUserSpecialtyTypeFromSpecialtyId,
    getCookiedInsuranceFromUserSpecialtyType,
} from '../../../utils/getInsuranceFromSpecialtyIds';
import { FLOW_SOURCE_ENUM } from '@zocdoc/components/src/InsurancePicker/data/dropdown-state/dropdownStateTracking';

const AWS_API_HOST = process.env.AWS_API_HOST || '';

const UNIVERSAL_SEARCH_ADDITIONAL_FIELDS = {
    namedSearchFiltering: 1,
    separateSemanticIndex: 1,
    useMostPopular: 1,
    trimResults: 1,
};
const MAX_EXPANDABLE_HEIGHT = 100; // Must be >= the tallest expandable; the animation becomes faster for larger numbers

class SearchBarView extends Component {
    static propTypes = {
        address: PropTypes.string.isRequired,
        clearCarrierAndPlan: PropTypes.func.isRequired,
        collapse: PropTypes.func,
        expand: PropTypes.func,

        /*
        * Search will normally be submitted by updating params in redux.
        * This is to handle submit when nothing has been updated but user clicks
        * search button again
        */
        fetchCarriersIfNeeded: PropTypes.func,
        fetchPlansIfNeeded: PropTypes.func,
        fetchPopularCarriers: PropTypes.func,
        fetchPopularPlansIfNeeded: PropTypes.func,
        forceSubmitSearch: PropTypes.func,
        insurancePickerErrorStateOverride: PropTypes.bool,
        isExpanded: PropTypes.bool,
        recordAssignment: PropTypes.func,
        searchOnSelect: PropTypes.bool,
        searchOrigin: PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
        searchQueryGuid: PropTypes.string,
        sessionId: PropTypes.string,
        setCarrier: PropTypes.func,
        setPlan: PropTypes.func,
        setInsuranceType: PropTypes.func,
        shouldPersistChosenCarrier: PropTypes.bool,
        showCollapseToggle: PropTypes.bool,
        specialtyId: PropTypes.string,
        specialtyInsuranceType: PropTypes.number,
        theme: PropTypes.object,
        trackConversion: PropTypes.func,
        trackMobilePpsClick: PropTypes.func,
        trackPpsChange: PropTypes.func,
        trackPpsFocus: PropTypes.func,
        trackPpsSelect: PropTypes.func,
        trackSearch: PropTypes.func,
        updateParameters: PropTypes.func.isRequired,
        updatePendingSearchParameters: PropTypes.func.isRequired,
        useChevronDropdownHeader: PropTypes.bool,
    };

    static defaultProps = {
        collapse: noop,
        expand: noop,
        fetchCarriersIfNeeded: noop,
        fetchPlansIfNeeded: noop,
        fetchPopularCarriers: noop,
        fetchPopularPlansIfNeeded: noop,
        forceSubmitSearch: noop,
        isExpanded: false,
        recordAssignment: noop,
        searchOnSelect: true,
        searchOrigin: {},
        setInsuranceType: noop,
        shouldPersistChosenCarrier: false,
        showCollapseToggle: true,
        trackConversion: noop,
        trackPpsChange: noop,
        trackPpsFocus: noop,
        trackPpsSelect: noop,
        trackSearch: noop,
        useChevronDropdownHeader: false,
    };

    static contextTypes = {
        store: PropTypes.object, // redux store
    };

    render() {
        const {
            recordAssignment,
            sessionId,
            trackConversion,
        } = this.props;

        const expanded = this.props.isExpanded;
        const shouldHaveSecretToggle = !this.props.isExpanded;
        const isMobile = isMobileBreakpoint();

        const recordPersistCarrierInCallbackAssignment = () =>
            recordAssignment(PINA_INSURANCE_PICKER_PERSIST_CHOSEN_CARRIER);

        const recordChevronAssignment = () => recordAssignment(PINA_INSURANCE_PICKER_USE_DROPDOWN_CHEVRON);
        const recordAbCarrierChosenByUser = () => trackConversion({
            conversionId: 'InsurancePicker_CarrierChosen',
            sessionId,
            url: `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
            userAgent: navigator.userAgent,
        });
        const recordAbPlanChosenByUser = () => trackConversion({
            conversionId: 'InsurancePicker_PlanChosen',
            sessionId,
            url: `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
            userAgent: navigator.userAgent,
        });

        return (
            <StyledForm
                action="#"
                {...filterToValidAttributes(this.props)}
                isExpanded={this.props.isExpanded}
                onSubmit={this.handleSubmit}
            >
                <PpsSearchFieldWrapper key="pps-field">
                    <Expandable expanded={expanded}>
                        <SearchFieldLabel>search</SearchFieldLabel>
                    </Expandable>
                    <UniversalSearch
                        additionalQueryParameters={UNIVERSAL_SEARCH_ADDITIONAL_FIELDS}
                        carrierId={this.props.carrierId}
                        data-test="search-bar-pps"
                        directoryId={this.props.theme.directoryId}
                        enableBrowseSpecialtyForMobile
                        enablePrefixSearch
                        enableSemanticSpecialty
                        isMobile={isMobile}
                        latitude={this.props.searchOrigin.latitude}
                        longitude={this.props.searchOrigin.longitude}
                        onChangeValue={this.handlePpsChange}
                        onDropdownItemClick={this.handlePpsDropdownClick}
                        onSelectOption={this.handlePpsSelectOption}
                        onTextBoxFocus={this.props.trackPpsFocus}
                        page="search"
                        planId={this.props.planId}
                        searchApiUrl={`${AWS_API_HOST}/directory/v1/providers/autocomplete`}
                        searchApiV2Url={`${AWS_API_HOST}/directory/v2/providers/autocomplete`}
                        searchQueryGuidGetter={this.getSearchQueryGuid}
                        sessionId={this.props.sessionId}
                        trackingId={this.props.trackingId}
                        useElasticGrouping
                    />
                    {/*
                        The first tap on the mobile PPS field should expand the fields, not set
                        focus to the textbox. Solution: Create a transparent div that covers the PPS bar
                        and disappears when expanded.
                    */}
                    {shouldHaveSecretToggle
                        ? <SecretToggle
                            data-uem-id="universal-search-mobile"
                            onClick={this.handleSecretToggleClick}
                        />
                        : null}
                </PpsSearchFieldWrapper>

                <LocationSearchFieldWrapper
                    key="location-field"
                    expanded={expanded}
                >
                    <SearchFieldLabel>location</SearchFieldLabel>
                    <StyledLocationPicker
                        biasPredictionsToCoordinates={this.getLocationPickerBiasCoordinates()}
                        data-test="search-bar-location"
                        data-uem-id="location-dropdown"
                        defaultValue={this.props.address}
                        gaCategory={pageName}
                        onBlur={this.handleLocationBlur}
                        onChange={this.handleLocationChange}
                        onClickPrediction={this.handleLocationDropdownClick}
                        section={EventTrackingConstants.TARGETS.SEARCH_BAR}
                    />
                </LocationSearchFieldWrapper>

                <InsuranceSearchFieldWrapper
                    key="insurance-picker-field"
                    expanded={expanded}
                >
                    <SearchFieldLabel>insurance</SearchFieldLabel>
                    <InsurancePicker
                        carrierId={this.props.carrierId}
                        data-test="search-bar-insurance"
                        errorStateOverride={this.props.insurancePickerErrorStateOverride}
                        gaCategory={pageName}
                        insuranceType={this.props.specialtyInsuranceType}
                        onChange={this.handleInsuranceChange}
                        planId={this.props.planId}
                        recordAbCarrierChosenByUser={recordAbCarrierChosenByUser}
                        recordAbPlanChosenByUser={recordAbPlanChosenByUser}
                        recordChevronAssignment={recordChevronAssignment}
                        recordPersistCarrierInCallbackAssignment={recordPersistCarrierInCallbackAssignment}
                        section={EventTrackingConstants.TARGETS.SEARCH_BAR}
                        shouldPersistCarrierInCallback={this.props.shouldPersistChosenCarrier}
                        showScanInsuranceCardButton
                        useChevronDropdownHeader={this.props.useChevronDropdownHeader}
                    />
                </InsuranceSearchFieldWrapper>

                <Expandable expanded={this.props.isExpanded}>
                    <SearchButton
                        data-test="search-bar-search-button"
                        data-uem-id="universal-search-submit"
                        onClick={this.handleSearchButtonClick}
                        title="Search for doctors"
                        type="submit"
                    >
                        <SearchButtonText>Find</SearchButtonText>
                        <StyledMagnifyingGlass color="currentColor" />
                    </SearchButton>

                    {this.props.showCollapseToggle ? (
                        <TextRight>
                            <CollapseToggle onClick={this.props.collapse}>
                                <StyledArrow
                                    color={this.props.theme.search.bar.mobileCollapseArrow.bgColor}
                                    direction="up"
                                />
                                <StyledClose>close</StyledClose>
                            </CollapseToggle>
                        </TextRight>
                    ) : null}
                </Expandable>
            </StyledForm>
        );
    }

    getSearchQueryGuid = () => this.props.searchQueryGuid;

    handleInsuranceChange = insurance => {
        const carrierId = get(insurance, 'carrier.id');
        const planId = get(insurance, 'plan.id');
        const pendingParams = this.getPendingParams();

        if (carrierId) {
            const nextState = {
                carrierId,
            };

            if (planId || pendingParams.planId) {
                nextState.planId = planId || pendingParams.planId;
            }

            this.props.updatePendingSearchParameters(nextState);

            const onlyRealCarrierSelected = carrierId > 0 && (nextState.planId <= 0);

            if (this.props.searchOnSelect && !onlyRealCarrierSelected) {
                this.handleSubmit();
            }
        }
    };

    handleLocationBlur = e => {
        // Need to set cookie location here before the fetch
        // as the cookie setting done in component-sdk occurs after the fetch
        const location = e.target.value;
        if (location) {
            const cookieOptions = isZocdocBrand(this.props.theme.directoryId)
                ? { domain: '.zocdoc.com' }
                : {};
            cookie.set(CookieNames.address, location, cookieOptions);
            // Refetch popular carriers, plans based on the new location
            this.props.fetchPopularCarriersIfNeeded(this.props.theme.directoryId, true);
            this.props.fetchPopularPlansIfNeeded(true);
        }
    }

    handleLocationChange = address => {
        this.props.updatePendingSearchParameters({
            address,
            geoShape: null,
            longitude: null,
            latitude: null,
        });
    };

    handleLocationDropdownClick = () => {
        if (this.props.searchOnSelect) {
            this.handleSubmit();
        }
    };

    handlePpsDropdownClick = () => {
        if (this.props.searchOnSelect) {
            this.handleSubmit();
        }
    };

    handlePpsSelectOption = option => {
        const selectionType = option.name === option.procedureName ? 'procedure' : 'specialty';

        this.props.trackPpsSelect({
            selection: option.name,
            selectionType,
        });

        let newPendingParams = {
            specialtyId: option.specialtyId,
            procedureId: option.procedureId,
            searchType: selectionType,
        };

        const curPendingParams = this.getPendingParams();

        const newUserSpecialtyInsuranceType = getUserSpecialtyTypeFromSpecialtyId(option.specialtyId);
        // Current specialty type is firstly from pending params, secondly search param state
        const curUserSpecialtyInsuranceType = getUserSpecialtyTypeFromSpecialtyId(
            curPendingParams.specialtyId || this.props.specialtyId
        );

        if (newUserSpecialtyInsuranceType !== curUserSpecialtyInsuranceType) {
            // User is switching to a new specialty insurance
            // This insuranceType prop only updates the insurance type for search reasons
            this.props.setInsuranceType(option.specialtyInsuranceTypeId);
            // Check cookies to see if user has existing insurance of the new specialty
            const cookiedInsurance = getCookiedInsuranceFromUserSpecialtyType(newUserSpecialtyInsuranceType);
            if (!cookiedInsurance) {
                this.props.clearCarrierAndPlan();
                newPendingParams.carrierId = '-1';
                newPendingParams.planId = '-1';
            } else {
                // Cannot send data via a prop update because there is no componentDidUpdate hook in
                // the InsurancePickerWrapper that handles changes in carrierId/planId.
                // However to avoid more side effects and bugs with other use cases of the insurance picker,
                // instead we'll scope necessary InsurancePicker actions here within the SearchBar
                this.props.selectCarrier(cookiedInsurance.carrierId);
                this.props.setCarrier({
                    id: cookiedInsurance.carrierId,
                    name: 'Loading...', // A loading string picker before carrierId name is fetched from redux or APIs
                }, true, FLOW_SOURCE_ENUM.Cookie);
                this.props.selectPlan(cookiedInsurance.planId);
                this.props.setPlan({ id: cookiedInsurance.planId }, true, FLOW_SOURCE_ENUM.Cookie);
                this.props.fetchCarriersIfNeeded();
                this.props.fetchPlansIfNeeded();
                newPendingParams = {
                    ...newPendingParams,
                    ...cookiedInsurance,
                };
            }
        }

        this.props.updatePendingSearchParameters(newPendingParams);
    };

    handleSearchButtonClick = e => {
        this.props.collapse(e);
        this.props.trackSearch(e);
    };

    handleSecretToggleClick = e => {
        this.props.expand(e);
        this.props.trackMobilePpsClick();
    }

    handleSubmit = async e => {
        if (e) e.preventDefault();
        const params = this.getPendingParams();
        const hasAddressParam = params.address !== ''
            || (typeof params.address === 'undefined' && this.props.address !== '');

        if (!hasAddressParam) {
            // Try to use geolocation API to get user's location
            const currentLatLng = await this.fetchCurrentLocation();
            this.props.updatePendingSearchParameters({ ...currentLatLng });
        }

        this.submitPendingSearchParams();
        if (isEmpty(params)) {
            this.props.forceSubmitSearch();
        }
    };

    handlePpsChange = value => {
        this.props.trackPpsChange({ query: value });
        this.props.updatePendingSearchParameters({ searchQuery: value });
    };

    async fetchCurrentLocation() {
        let location = {};
        try {
            const locationRes = await fetchCurrentLocation();
            location = {
                latitude: locationRes.latitude,
                longitude: locationRes.longitude,
            };
        } catch (err) {
            /*
            * Leave address empty, and will default to IP
            * Address, coordinates and geoShape will already be nulled out
            * here after location is changed
            */
        }

        return location;
    }

    getLocationPickerBiasCoordinates() {
        return {
            lat: this.props.searchOrigin.latitude,
            lng: this.props.searchOrigin.longitude,
        };
    }

    submitPendingSearchParams() {
        this.props.updateParameters(this.getPendingParams(), { resetFilters: true });
    }

    /**
     * Since events in React are executed synchronously, when clicking an item in one
     * of the dropdowns, the pendingParameters prop update would not be received before
     * the click handler is executed. However, the reducer is also updated synchronously,
     * so we can just use the state selector to grab the most recent data.
     */
    getPendingParams = () => getPendingSearchParameters(this.context.store.getState());
}

const fieldOverrides = css`
    background-color: ${p => p.theme.search.bar.field.backgroundColor};
    border-color: ${p => p.theme.search.bar.field.borderColor};
    color: ${p => p.theme.search.bar.field.color};

    &::placeholder {
        color: ${p => p.theme.search.bar.field.placeholderColor};
    }

    &:focus::placeholder {
        color: ${p => p.theme.search.bar.field.placeholderColorFocus};
    }

    &:focus {
        background-color: ${p => p.theme.search.bar.field.backgroundColorFocus};
        border-color: ${p => p.theme.search.bar.field.borderColorFocus};
        color: ${p => p.theme.search.bar.field.colorFocus};
    }
`;

/**
 * In a nutshell, these overrides should never be applied to input when the picker
 * is in "takeover" mode. Since this is purely a function of CSS and we aren't rendering
 * a different layout, the selectors have to be composed a little more artfully.
 *
 * InsurancePicker is the exception, as it does actually render a separate input for
 * the dropdown.
*/
const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    max-width: 100%;

    [data-test="insurance-picker-textbox"],
    [data-test="selected-state-section"] {
        ${fieldOverrides};
    }

    [data-test="takeover-input"],
    [data-test="universal-search"] {
        &[data-dropdown-active="true"] {
            ${breakpoint.small} {
                [data-test="universal-search-text-box"],
                [data-test="takeover-input-input"] {
                    ${fieldOverrides};
                }
            }
        }

        &[data-dropdown-active="false"] {
            [data-test="universal-search-text-box"],
            [data-test="takeover-input-input"] {
                ${fieldOverrides};
            }
        }
    }

    ${breakpoint.medium} {
        flex-direction: row;
    }
`;

const Expandable = styled.div`
    ${breakpoint('small', { down: true })} {
        max-height: ${p => p.expanded ? MAX_EXPANDABLE_HEIGHT : '0'}px;
        opacity: ${p => p.expanded ? '1' : '0'};
        transition: ${p => p.expanded ? 'all 0.5s' : 'none'};
    }
`;

export const SearchFieldWrapper = styled.div`
    line-height: 0;
    position: relative; // To contain the secret toggle
    flex-grow: 1;

    ${breakpoint.medium} {
        &:not(:first-child) {
            margin-left: 1px;
        }
    }
`;

export const SearchFieldLabel = styled.h4`
    padding-top: 14px;
    padding-bottom: 9px;
    font: 13px/1.3 ${FontFamily.SharpSansSemibold};

    ${breakpoint.medium} {
        display: none;
    }
`;

const StyledLocationPicker = styled(LocationPicker)`
    z-index: 10;
`;

const ExpandableSearchFieldWrapper = Expandable.withComponent(SearchFieldWrapper);

const PpsSearchFieldWrapper = styled(SearchFieldWrapper)`
    ${breakpoint.medium} {
        flex-grow: 11;
    }
`;

const LocationSearchFieldWrapper = styled(ExpandableSearchFieldWrapper)`
    ${breakpoint.medium} {
        flex-grow: 5;
    }
`;

const InsuranceSearchFieldWrapper = styled(ExpandableSearchFieldWrapper)`
    ${breakpoint.medium} {
        flex-grow: 7;
    }
`;

const CollapseToggle = styled.span`
    display: inline-block;
    position: relative;
    bottom: -8px;
    padding: 4px;
    text-align: right;
    cursor: pointer;

    ${breakpoint.medium} {
        display: none;
    }
`;

const TextRight = styled.div`
    text-align: right;
`;

const StyledArrow = styled(Arrow)`
    font-size: 8px;
`;

const StyledClose = styled.span`
    margin-left: 10px;
    font: 13px/1.3 ${FontFamily.SharpSansSemibold};
`;

const SecretToggle = styled.div`
    z-index: 1;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    cursor: text;

    ${breakpoint.medium} {
        display: none;
    }
`;

const fontSize = 24;

export const SearchButton = styled.button`
    background-color: ${p => p.theme.search.bar.button.bgColor};
    border: 0;
    border-radius: 0;
    color: ${p => p.theme.search.bar.button.iconColor};
    cursor: pointer;
    font: ${fontSize}px/1 ${FontFamily.SharpSansBold};
    margin-top: 16px;
    padding: ${(INPUT_HEIGHT - fontSize) / 2}px 0;
    text-align: center;
    width: 100%;

    &:hover {
        color: ${p => p.theme.search.bar.button.iconColorHover};
        background-color: ${p => p.theme.search.bar.button.bgColorHover};
    }

    &:active {
        color: ${p => p.theme.search.bar.button.iconColorPressed};
        background-color: ${p => p.theme.search.bar.button.bgColorPressed};
    }

    ${breakpoint.medium} {
        margin-top: 0;
        padding: 0;
        height: ${INPUT_HEIGHT}px;
        width: ${INPUT_HEIGHT}px;
    }
`;

const SearchButtonText = styled.span`
    ${breakpoint.medium} {
        display: none;
    }
`;

const StyledMagnifyingGlass = styled(MagnifyingGlass)`
    box-sizing: content-box !important;
    display: none;
    font-size: 24px;
    width: 100%;

    ${breakpoint.medium} {
        display: block;
    }
`;

export default withTheme(SearchBarView);
